import { useEffect, useState } from 'react'
import { Box, Text, Modal, useT } from '@minetrace/ui'
import { useFormColors } from '@minetrace/ui/forms'
import { componentTypesService } from '../api/services'
import type { ComponentTypeItem } from '../api/models/maestros.models'
import type { MiningRightComponentResponseItem } from '../api/models/response/proveedor'
import ComponentsEditor, { type ComponentEditorItem, type ComponentTypeOption } from './ComponentsEditor'

interface ComponentsModalProps {
  open: boolean
  onClose: () => void
  miningRightName: string
  utmZone: number
  polygonGeoJson: string | null
  components: MiningRightComponentResponseItem[]
  onAdd: (data: { componentTypeId: string; name?: string; northing: number; easting: number }) => Promise<MiningRightComponentResponseItem>
  onRemove: (componentId: string) => Promise<void>
}

const ComponentsModal = ({ open, onClose, miningRightName, utmZone, polygonGeoJson, components, onAdd, onRemove }: ComponentsModalProps) => {
  const c = useFormColors()
  const { t } = useT()
  const [availableTypes, setAvailableTypes] = useState<ComponentTypeOption[]>([])

  // Los componentes de un derecho minero son siempre de explotación
  // (bocaminas, tolvas, piques). El backend (AddMiningRightComponent) rechaza
  // cualquier otra categoría. Inferir "beneficio" del nombre del DM era
  // engañoso — el catálogo de beneficio pertenece a plantas, no a DMs.
  const categoryCode = 'explotacion'

  useEffect(() => {
    if (!open) return
    let cancelled = false
    componentTypesService
      .getAll({ categoryCode, pageSize: 200 })
      .then((res) => {
        if (cancelled) return
        const opts: ComponentTypeOption[] = res.items.map((it: ComponentTypeItem) => ({
          value: it.id,
          label: it.name,
          tierCode: it.tierCode,
          tierName: it.tierName,
          description: it.description,
        }))
        setAvailableTypes(opts)
      })
      .catch(() => { /* silent — picker just shows empty */ })
    return () => { cancelled = true }
  }, [open, categoryCode])

  const editorItems: ComponentEditorItem[] = components.map((comp) => ({
    id: comp.id,
    componentTypeId: comp.componentTypeId,
    componentTypeName: comp.componentTypeName,
    componentTypeCode: comp.componentTypeCode,
    name: comp.name,
    northing: comp.northing,
    easting: comp.easting,
    isOutsidePolygon: comp.isOutsidePolygon,
  }))

  const handleAdd = async (data: { componentTypeId: string; name?: string; northing: number; easting: number }): Promise<ComponentEditorItem> => {
    const result = await onAdd(data)
    return {
      id: result.id,
      componentTypeId: result.componentTypeId,
      componentTypeName: result.componentTypeName,
      componentTypeCode: result.componentTypeCode,
      name: result.name,
      northing: result.northing,
      easting: result.easting,
      isOutsidePolygon: result.isOutsidePolygon,
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('shared.componentsModal.title')}
      subtitle={t('shared.componentsModal.subtitle')}
      size="xl"
      footer={
        <Box as="button" type="button" px={6} py={2} bg="#e0e3e1" color="#2a2622" borderRadius="lg"
          fontSize="sm" fontWeight="600" cursor="pointer" _hover={{ bg: '#d0d4d2' }}
          onClick={onClose}
        >
          {t('shared.componentsModal.close')}
        </Box>
      }
    >
      <Text tid="shared.componentsModal.breadcrumb" values={{ name: miningRightName }} fontSize="xs" color={c.textMuted} mb={4} />

      <ComponentsEditor
        utmZone={utmZone}
        polygonGeoJson={polygonGeoJson}
        components={editorItems}
        availableTypes={availableTypes}
        onAdd={handleAdd}
        onRemove={onRemove}
      />
    </Modal>
  )
}

export default ComponentsModal
